<template>
  <div class="language-selector" ref="wrapper">
    <VueSelect
      v-model="selectedLanguage"
      :options="options"
      @option-selected="changeLanguage"
      :isSearchable="false"
      :isClearable="false"
      :placeholder="t('header.languageSelector.placeholder')"
      class="language-select"
      @menu-opened="handleSelectOpen"
      @menu-closed="handleSelectClose"
    >
      <template #value="{ option }">
        <span>{{ compactDisplay ? option.value.toUpperCase() : option.label }}</span>
      </template>
      <template #option="{ option }">
        <span
          :style="{
            fontWeight: option.label === selectedLanguage ? '700' : '500',
            color:
              option.value === selectedLanguage ? 'var(--menu-item-color)' : 'var(--text-color)',
          }"
        >
          {{ option.label }}
        </span>
      </template>
    </VueSelect>
  </div>
</template>

<script setup lang="ts">
import { nextTick, ref, watch } from 'vue'
import type { Language } from '../types/configuration/Language'
import { loadAndSetLocale } from '../i18n'
import VueSelect from 'vue3-select-component'
import type { Option } from 'vue3-select-component'
import { t } from './../composables/Translation'
import { useAppStore } from '../stores/appStore'

const appStore = useAppStore()

const props = defineProps<{
  model: Language[] | undefined
  compactDisplay?: boolean
  reloadPage?: () => void
}>()

let options: Option<string>[] = []

const selectedLanguage = ref('en')
const wrapper = ref(null)
const isMenuOpened = ref(false)

const changeLanguage = async function () {
  const newLanguage = selectedLanguage.value

  if (newLanguage === appStore.selectedLanguage) {
    return
  }

  appStore.setLanguage(newLanguage)
  await loadAndSetLocale(newLanguage)
  if (props.reloadPage) {
    props.reloadPage()
  } else {
    window.location.reload()
  }
}

// Watch for store value changes
watch(
  () => appStore.selectedLanguage,
  async newLang => {
    if (selectedLanguage.value !== newLang) {
      selectedLanguage.value = newLang
      await loadAndSetLocale(newLang)
    }
  },
  { immediate: true }
)

watch(
  () => props.model,
  async () => {
    if (props.model) {
      options = props.model.map((l: Language) => {
        return { label: l.languageName, value: l.fileName }
      })
    }
  },
  { immediate: true }
)

async function handleSelectOpen() {
  await nextTick()
  if (!isMenuOpened.value && wrapper && wrapper.value) {
    const dropdown = (wrapper.value as any).querySelector('.language-select .menu')
    const rect = dropdown.getBoundingClientRect()

    // set dynamically the position of the menu (if there is not enough space on the right, the menu will be moved to the left)
    if (rect.right > window.innerWidth) {
      dropdown.style.setProperty('left', 'auto', 'important')
      dropdown.style.setProperty('right', '0', 'important')
    } else {
      dropdown.style.removeProperty('left')
      dropdown.style.removeProperty('right')
    }
    isMenuOpened.value = true
  }
}

async function handleSelectClose() {
  isMenuOpened.value = false
}
</script>

<style scoped lang="scss">
:deep(.vue-select .input-container input) {
  display: none !important;
}

:deep(.vue-select .menu) {
  min-width: 155px !important;
}

.language-selector {
  cursor: pointer;
  margin-top: 4px; // to center the elements in the header

  .language-select {
    font-family: 'Noto Sans', sans-serif;
    --vs-font-size: 14px;
    --vs-font-weight: 500;
    --vs-option-padding: 12px;
    --vs-option-font-size: 14px;
    --vs-option-font-weight: 500;
    --vs-option-opacity-menu-open: 1;
    --vs-background-color: var(--background-color);
    --vs-text-color: var(--text-color);
    --vs-indicator-icon-color: var(--text-color);
    --vs-option-hover-background-color: var(--text-background-color);
    --vs-option-focused-background-color: var(--text-background-color);
    --vs-option-selected-background-color: var(--text-background-color);

    --vs-border-radius: 8px;
    --vs-border: 1px solid var(--background-color);
    --vs-outline-width: 1px;
    --vs-outline-color: var(--background-color);

    --vs-menu-border: 1px solid var(--border-color);
    --vs-menu-background-color: var(--background-color);
  }
}
</style>
